import {Box, Typography} from '@mui/material';
import {FormikConfig} from 'formik';
import {FC, useEffect, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {NavigateFunction, useLocation, useNavigate} from 'react-router-dom';
import {authApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {LangSelect} from 'src/components/common/LangSelect';
import {ResetPasswordType} from 'src/components/forms/formData/resetPassword';
import {ResetPasswordForm} from 'src/components/forms/ResetPasswordForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
// The same layout as the reset page, from the same module rather than a second copy of it: these
// two screens are the same screen with a different verb, and a copy is what lets them drift apart.
import {
    langSelectWrapperSx,
    resetPasswordDescriptionSx,
    welcomeBlockSx,
    wrapperSx,
} from '../ResetPassword/ResetPassword.sx';

type SetPasswordMutationTrigger = ReturnType<typeof authApi.useSetCompanyCredentialPasswordMutation>[0];

const handleSubmit =
    (
        trigger: SetPasswordMutationTrigger,
        dispatch: AppDispatch,
        navigate: NavigateFunction,
        successMessage: string,
        errorMessage: string,
        hash?: string
    ): FormikConfig<ResetPasswordType>['onSubmit'] =>
        async ({password}) => {
            if (!hash) {
                return NotifyError(errorMessage);
            }
            const data = {
                newPassword: password,
            };
            const params = {
                hash,
            };
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({
                    data,
                    params,
                }).unwrap();
                NotifySuccess(successMessage);
                // To the login form, not into the application: setting the password issues no
                // session by design, so there is nothing here to land in.
                navigate('/login');
            } catch (error: any) {
                NotifyError(error.data.message);
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const SetPassword: FC = () => {
    const intl = useIntl();
    const {search} = useLocation();

    // Parsed as a query string rather than by pattern. The regex the reset page uses takes
    // everything after the last "=", which is the hash only while the link carries exactly one
    // parameter - an invitation link that also names a language would have handed the server the
    // language instead.
    const hash = useMemo(() => {
        return new URLSearchParams(search).get('hash') ?? undefined;
    }, [search]);

    const [trigger] = authApi.useSetCompanyCredentialPasswordMutation();
    const successMessage = intl.formatMessage({id: 'successfullyChanged'});
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const errorMessage = intl.formatMessage({id: 'somethingWrong'});

    useEffect(() => {
        !hash && navigate('/login');
    }, [hash]);

    return (
        <Box sx={wrapperSx}>
            <Box sx={langSelectWrapperSx}>
                <LangSelect/>
            </Box>
            <Box sx={welcomeBlockSx}>
                <Typography
                    variant="h1"
                    color="textPrimary"
                >
                    <FormattedMessage id="setPassword"/>
                </Typography>

                <Typography
                    variant="body1"
                    color="text.primary"
                    sx={resetPasswordDescriptionSx}
                >
                    <FormattedMessage id="setPasswordDescription"/>
                </Typography>
            </Box>
            <ResetPasswordForm
                handleSubmit={handleSubmit(trigger, dispatch, navigate, successMessage, errorMessage, hash)}
            />
        </Box>
    );
};
